// ToolContext.tsx - TypeScript version of the Tool Context with Brush Types
import React, { createContext, useContext, useState } from 'react';

// Define tool modes
export const TOOL_MODES = {
  DRAW: 'draw',           // Free drawing
  LINE: 'line',           // Straight line
  RECTANGLE: 'rectangle', // Rectangle shape
  CIRCLE: 'circle',       // Circle shape
  ERASER: 'eraser',       // Eraser tool
  BUBBLE: 'bubble',       // Add speech bubble
  TEXT: 'text',           // Add/edit text
  SELECT: 'select',       // Select elements
  STAMP: 'stamp',         // Place character/prop
  EYEDROPPER: 'eyedropper' // Pick color
} as const;

// TypeScript type for tool modes
export type ToolMode = typeof TOOL_MODES[keyof typeof TOOL_MODES];

// Define speech bubble types
export const BUBBLE_TYPES = {
  SPEECH: 'speech',       // Regular speech bubble
  THOUGHT: 'thought',     // Thought bubble
  NARRATION: 'narration', // Narration box
  SHOUT: 'shout',         // Shouting/loud voice
  WHISPER: 'whisper'      // Quiet speech
} as const;

// TypeScript type for bubble types
export type BubbleType = typeof BUBBLE_TYPES[keyof typeof BUBBLE_TYPES];

// Define brush types - NEW!
export const BRUSH_TYPES = {
  SMOOTH: 'smooth',       // Clean digital brush
  CRAYON: 'crayon',       // Textured crayon
  MARKER: 'marker',       // Semi-transparent marker
  CHALK: 'chalk',         // Dusty chalk
  PAINTBRUSH: 'paintbrush', // Bristled paint brush
  PENCIL: 'pencil'        // Graphite pencil
} as const;

// TypeScript type for brush types - NEW!
export type BrushType = typeof BRUSH_TYPES[keyof typeof BRUSH_TYPES];

// Character type definition
export interface Character {
  id: string;
  name: string;
  image: string;
  width: number;
  height: number;
  category?: string;
  flipped?: boolean;
  scale?: number;
  variantId?: string;
  variantName?: string;
  [key: string]: any; // Allow for additional properties
}

// Prop type definition
export interface Prop {
  id: string;
  name: string;
  image: string;
  width: number;
  height: number;
  category?: string;
  rotation?: number;
  scale?: number;
  [key: string]: any; // Allow for additional properties
}

// Action for undo/redo stack
export interface ToolAction {
  type: string;
  pageIndex: number;
  [key: string]: any; // Allow for additional properties based on action type
}

// Tool state interface - ENHANCED with brush type
export interface ToolState {
  currentMode: ToolMode;
  selectedColor: string;
  selectedBgColor: string;
  brushSize: number;
  brushType: BrushType; // NEW!
  eraserSize: number;
  bubbleType: BubbleType;
  fontSize: number;
  fontFamily: string;
  selectedElement: string | null;
  selectedElementType: string | null;
  selectedCharacter: Character | null;
  selectedProp: Prop | null;
  undoStack: ToolAction[];
  redoStack: ToolAction[];
}

// Tool context interface - ENHANCED with brush type
export interface ToolContextType {
  // State
  currentMode: ToolMode;
  selectedColor: string;
  selectedBgColor: string;
  brushSize: number;
  brushType: BrushType; // NEW!
  eraserSize: number;
  bubbleType: BubbleType;
  fontSize: number;
  fontFamily: string;
  selectedElement: string | null;
  selectedElementType: string | null;
  selectedCharacter: Character | null;
  selectedProp: Prop | null;
  hasUndo: boolean;
  hasRedo: boolean;
  
  // Methods
  setMode: (mode: ToolMode) => void;
  setColor: (color: string) => void;
  setBgColor: (color: string) => void;
  setBrushSize: (size: number) => void;
  setBrushType: (type: BrushType) => void; // NEW!
  setEraserSize: (size: number) => void;
  setBubbleType: (type: BubbleType) => void;
  setFontSize: (size: number) => void;
  setFontFamily: (family: string) => void;
  selectElement: (id: string, type: string) => void;
  clearSelection: () => void;
  selectCharacter: (character: Character) => void;
  selectProp: (prop: Prop) => void;
  addToUndoStack: (action: ToolAction) => void;
  undo: () => ToolAction | undefined;
  redo: () => ToolAction | undefined;
}

// Initial tool settings - ENHANCED with brush type
const initialToolState: ToolState = {
  currentMode: TOOL_MODES.DRAW,
  selectedColor: '#000000',
  selectedBgColor: 'transparent',
  brushSize: 3,
  brushType: BRUSH_TYPES.SMOOTH, // NEW!
  eraserSize: 20,
  bubbleType: BUBBLE_TYPES.SPEECH,
  fontSize: 16,
  fontFamily: 'Comic Sans MS, cursive',
  selectedElement: null,
  selectedElementType: null,
  selectedCharacter: null,
  selectedProp: null,
  undoStack: [],
  redoStack: []
};

// Create the context with default value
const ToolContext = createContext<ToolContextType>({} as ToolContextType);

// Provider component
export const ToolProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toolState, setToolState] = useState<ToolState>(initialToolState);
  
  // Set the current drawing mode
  const setMode = (mode: ToolMode) => {
    // Clear selection if switching away from text/select mode
    if ((toolState.currentMode === TOOL_MODES.TEXT || 
         toolState.currentMode === TOOL_MODES.SELECT) && 
        mode !== TOOL_MODES.TEXT && 
        mode !== TOOL_MODES.SELECT) {
      setToolState({
        ...toolState,
        currentMode: mode,
        selectedElement: null,
        selectedElementType: null
      });
    } else {
      setToolState({
        ...toolState,
        currentMode: mode
      });
    }
  };
  
  // Set drawing color
  const setColor = (color: string) => {
    setToolState({
      ...toolState,
      selectedColor: color
    });
  };
  
  // Set background color
  const setBgColor = (color: string) => {
    setToolState({
      ...toolState,
      selectedBgColor: color
    });
  };
  
  // Set brush size
  const setBrushSize = (size: number) => {
    setToolState({
      ...toolState,
      brushSize: size
    });
  };
  
  // Set brush type - NEW!
  const setBrushType = (type: BrushType) => {
    setToolState({
      ...toolState,
      brushType: type
    });
  };
  
  // Set eraser size
  const setEraserSize = (size: number) => {
    setToolState({
      ...toolState,
      eraserSize: size
    });
  };
  
  // Set bubble type
  const setBubbleType = (type: BubbleType) => {
    setToolState({
      ...toolState,
      bubbleType: type
    });
  };
  
  // Set font size
  const setFontSize = (size: number) => {
    setToolState({
      ...toolState,
      fontSize: size
    });
  };
  
  // Set font family
  const setFontFamily = (fontFamily: string) => {
    setToolState({
      ...toolState,
      fontFamily: fontFamily
    });
  };
  
  // Select an element (bubble, character, prop, etc.)
  const selectElement = (id: string, type: string) => {
    // If selecting a bubble, switch to text mode
    const newMode = type === 'bubble' ? TOOL_MODES.TEXT : TOOL_MODES.SELECT;
    
    setToolState({
      ...toolState,
      currentMode: newMode,
      selectedElement: id,
      selectedElementType: type
    });
  };
  
  // Clear the current selection
  const clearSelection = () => {
    setToolState({
      ...toolState,
      selectedElement: null,
      selectedElementType: null
    });
  };
  
  // Select a character from the library
  const selectCharacter = (character: Character) => {
    setToolState({
      ...toolState,
      currentMode: TOOL_MODES.STAMP,
      selectedCharacter: character,
      selectedProp: null // Clear prop selection
    });
  };
  
  // Select a prop from the library
  const selectProp = (prop: Prop) => {
    setToolState({
      ...toolState,
      currentMode: TOOL_MODES.STAMP,
      selectedProp: prop,
      selectedCharacter: null // Clear character selection
    });
  };
  
  // Add action to undo stack
  const addToUndoStack = (action: ToolAction) => {
    setToolState({
      ...toolState,
      undoStack: [...toolState.undoStack, action],
      redoStack: [] // Clear redo stack when new action is performed
    });
  };
  
  // Undo last action
  const undo = () => {
    if (toolState.undoStack.length === 0) return;
    
    const lastAction = toolState.undoStack[toolState.undoStack.length - 1];
    setToolState({
      ...toolState,
      undoStack: toolState.undoStack.slice(0, -1),
      redoStack: [...toolState.redoStack, lastAction]
    });
    
    // Return the action so it can be undone by the project context
    return lastAction;
  };
  
  // Redo last undone action
  const redo = () => {
    if (toolState.redoStack.length === 0) return;
    
    const lastRedoAction = toolState.redoStack[toolState.redoStack.length - 1];
    setToolState({
      ...toolState,
      redoStack: toolState.redoStack.slice(0, -1),
      undoStack: [...toolState.undoStack, lastRedoAction]
    });
    
    // Return the action so it can be redone by the project context
    return lastRedoAction;
  };
  
  // Value object with state and all helper functions - ENHANCED with brush type
  const value: ToolContextType = {
    // State
    currentMode: toolState.currentMode,
    selectedColor: toolState.selectedColor,
    selectedBgColor: toolState.selectedBgColor,
    brushSize: toolState.brushSize,
    brushType: toolState.brushType, // NEW!
    eraserSize: toolState.eraserSize,
    bubbleType: toolState.bubbleType,
    fontSize: toolState.fontSize,
    fontFamily: toolState.fontFamily,
    selectedElement: toolState.selectedElement,
    selectedElementType: toolState.selectedElementType,
    selectedCharacter: toolState.selectedCharacter,
    selectedProp: toolState.selectedProp,
    hasUndo: toolState.undoStack.length > 0,
    hasRedo: toolState.redoStack.length > 0,
    
    // Actions
    setMode,
    setColor,
    setBgColor,
    setBrushSize,
    setBrushType, // NEW!
    setEraserSize,
    setBubbleType,
    setFontSize,
    setFontFamily,
    selectElement,
    clearSelection,
    selectCharacter,
    selectProp,
    addToUndoStack,
    undo,
    redo
  };
  
  return (
    <ToolContext.Provider value={value}>
      {children}
    </ToolContext.Provider>
  );
};

// Custom hook for using this context
export const useTool = (): ToolContextType => {
  const context = useContext(ToolContext);
  if (!context) {
    throw new Error('useTool must be used within a ToolProvider');
  }
  return context;
};